import { Show, createSignal } from 'solid-js';
import { FormattedMessage } from '../Markdown/FormattedMessage';
import { AnswerResponse } from './AnswerResponse';
import {
  Feedback,
  NextBestAction,
  SearchIntent,
  WIDGET_TYPE,
} from '@/types/constants';
import { NextBestActions } from './NextBestActions';
import { Disclaimer } from './Disclaimer';
import { SparkleIcon } from '@/components/Icons/SparkleIcon';
import { CopyIcon } from '@/components/Icons/CopyIcon';
import { DEFAULT_NO_ANSWER_RESPONSE } from '@/constants';
import { isMissingResponse } from '@/utils/common';
import { replaceAttachmentPath } from '@/utils/replaceAttachmentPath';
import { EventTracker } from '@/utils/event-tracker-store';

type AnswerCardProps = {
  answer: string;
  answering?: boolean;
  identifyingIntent?: boolean;
  showDisclaimer?: boolean;
  disclaimer?: string;
  hideFeedback?: boolean;
  apiHost: string;
  apiKey: string;
  onFeedback: (feedback: Feedback) => void;
  onFeedbackReasonSubmit: (reason: string) => void;
  nextBestActions?: NextBestAction[];
  searchQueryId?: string;
  searchQuery?: string;
  promptId?: string;
  eventTracker?: EventTracker;
  answerNotFoundMessage?: string;
  searchIntent?: SearchIntent;
  onAskNewQuestion?: () => void;
  generatingAnswerText?: string;
  suggestedAnswerText?: string;
  widgetType?: WIDGET_TYPE;
};

export const AnswerCard = (props: AnswerCardProps) => {
  const [nextBestActions] = createSignal(props.nextBestActions || []);
  const [isCopied, setIsCopied] = createSignal(false);

  const getMessage = (answer: string) => {
    if (props.answering) {
      return props.answer; // return raw answer while streaming
    }

    if (isMissingResponse(answer)) {
      return props.answerNotFoundMessage || DEFAULT_NO_ANSWER_RESPONSE;
    }

    return replaceAttachmentPath(answer, props.apiHost);
  };

  const contentRef: any = null;
  const copyToClipboard = () => {
    const range = document.createRange();
    if (!contentRef) {
      return;
    }
    range.selectNode(contentRef);
    window.getSelection()?.removeAllRanges();
    window.getSelection()?.addRange(range);
    document.execCommand('copy');
    window.getSelection()?.removeAllRanges();
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 1000);
  };

  return (
    <div class="block relative w-full w-lg p-[16px] bg-white border border-[var(--bfsearch-border-color)] rounded-md">
      <div class="group flex h-full flex-col justify-between overflow-hidden">
        <p class="text-[16px] font-semibold text-primary line-height-[24px] flex flex-row gap-2 mb-2">
          <SparkleIcon answering={props.answering} />
          {props.answering
            ? props.generatingAnswerText || 'Generating answer...'
            : props.suggestedAnswerText || 'Suggested answer'}
        </p>
        <div ref={contentRef}>
          <FormattedMessage
            message={getMessage(props.answer)}
            isStreaming={props.answering || false}
            widgetType={props.widgetType}
          />
        </div>
        <Show when={!props.answering && !props.hideFeedback}>
          <div
            class="absolute top-4 right-4 cursor-pointer group flex"
            onClick={copyToClipboard}
          >
            <CopyIcon />
            <Show when={isCopied()}>
              <span class="absolute top-[30px] right-0 bg-white text-gray-600 p-1 rounded-md text-xs -mt-2 -mr-2">
                Copied
              </span>
            </Show>
          </div>
          <AnswerResponse
            onFeedback={props.onFeedback}
            onFeedbackReasonSubmit={props.onFeedbackReasonSubmit}
          />
        </Show>
        <Show
          when={
            (nextBestActions() && nextBestActions().length > 0) ||
            (props.searchIntent && props.searchIntent.actions.length > 0)
          }
        >
          <NextBestActions
            loading={props.answering || props.identifyingIntent}
            actions={nextBestActions()}
            apiHost={props.apiHost}
            apiKey={props.apiKey}
            searchQueryId={props.searchQueryId}
            searchQuery={props.searchQuery}
            promptId={props.promptId}
            answer={props.answer}
            searchIntent={props.searchIntent}
            onAskNewQuestion={props.onAskNewQuestion}
          />
        </Show>
        <Show when={!props.answering && props.showDisclaimer}>
          <Disclaimer message={props.disclaimer} />
        </Show>
      </div>
    </div>
  );
};
